import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ConfigurationRelease, PCInstance, StartedEntry } from '../types/database';
import {
  dequeueForLaunch,
  displayName as queueDisplayName,
  fullName,
  isAlreadyStartedError,
  isNotQueuedError,
  partyMarker,
} from '../services/queue';
import {
  crashRecoveryView,
  currentOperatorId,
  queueMove,
  queueRequeue,
  queueRestart,
  type TileRecoveryDeps,
} from '../services/tileRecovery';
import {
  FAKE_NEARLY_DONE_LEAD_MS,
  fakeAgentArmStuck,
  fakeAgentCrash,
  fakeAgentLaunch,
  fakeAgentMarkNearlyDone,
  fakeAgentStop,
  fakeCrashRecovery,
  getFakeAgent,
  subscribeFakeAgent,
  fakeAgentArmNoVrOutcome,
  fakeAgentNoVr,
  fakeAgentNoVrDismiss,
  fakeAgentNoVrKick,
  fakeAgentNoVrRepair,
  type FakeNoVrOutcome,
} from '../services/fakeAgent';
import { useQueueDequeueOptional } from '../contexts/QueueDequeueContext';
import { useQueueDispatchOptional } from '../contexts/QueueDispatchContext';
import { useSettings } from '../contexts/SettingsContext';
import { DEV_TOOLS_ENABLED, readFakeLoadingSeconds, readFakeRunSeconds } from '../utils/env';
import { broadcastNextUp } from './NextUpWidget';
import { nameToColor, playDangerAlert } from './PCTile';
import PCBarView, { type PCBarAction, type PCBarState, type PCBarVisitor } from './PCBarView';
import InterveneSheet, { type InterveneAction } from './InterveneSheet';
import LanguageFlag, { hasLanguageFlag } from './LanguageFlag';
import NoVrVerifyModal, { type NoVrSaw, type NoVrVerdictKind } from './NoVrVerifyModal';
import type { Notification } from '../types/database';
import type { ToastData } from './Toast';

// STAGING-ONLY stand-in for PCTile: a room PC without an ip_address (the
// staging isolation script wipes venue addresses) gets this tile when
// Settings -> Staging tools -> "Fake PC tiles" is on. Its "agent" is the
// in-memory store in services/fakeAgent.ts: idle -> loading -> running ->
// done -> idle, with the same queue wiring a real tile has:
//  - reports {online, running, canLaunch} to QueueDispatchContext and
//    registers a launch function, so the "Volgende bezoeker" pop-up lists it
//    as a free PC and launches THROUGH it (pinned dequeue with this pc id),
//  - shows the visitor playing here (fn_get_started_today via startedByPc),
//  - offers Opnieuw starten / Verplaats naar andere PC / Terug in wachtrij
//    through the shared services/tileRecovery helper,
//  - fires the nearly-done broadcast (banner + display overlay + chime) 15 s
//    before the fake run ends, like PCTile does on last_level_warning.
// Never calls pcAgent. Gated on DEV_TOOLS_ENABLED (also in Dashboard).

interface FakePCTileProps {
  pcInstance: PCInstance;
  /** The room's configured releases: every one counts as installed here. */
  experiences: ConfigurationRelease[];
  isAdmin?: boolean;
  onExperienceStateChange?: (pcId: string, running: boolean) => void;
  onToast?: (t: ToastData) => void;
  /** Same switch PCTile takes: 'bar' renders the queue-mode row instead of the
   *  tile, so the staging drill exercises the queue dashboard too. */
  variant?: 'tile' | 'bar';
}

function useFakeAgent(pcId: string) {
  return useSyncExternalStore(
    (cb) => subscribeFakeAgent(pcId, cb),
    () => getFakeAgent(pcId),
    () => getFakeAgent(pcId),
  );
}

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

function FakePCTile({
  pcInstance,
  experiences,
  isAdmin = false,
  onExperienceStateChange,
  onToast,
  variant = 'tile',
}: FakePCTileProps) {
  const state = useFakeAgent(pcInstance.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [languagePick, setLanguagePick] = useState<ConfigurationRelease | null>(null);
  const [showIntervene, setShowIntervene] = useState(false);

  const queueCtx = useQueueDequeueOptional();
  const queueCtxRef = useRef(queueCtx);
  useEffect(() => {
    queueCtxRef.current = queueCtx;
  }, [queueCtx]);
  // Same ref pattern as PCTile: the report/registry effects must not re-fire
  // on every pcReports change of the context value.
  const dispatchCtx = useQueueDispatchOptional();
  const dispatchCtxRef = useRef(dispatchCtx);
  useEffect(() => {
    dispatchCtxRef.current = dispatchCtx;
  }, [dispatchCtx]);
  const onExperienceStateChangeRef = useRef(onExperienceStateChange);
  useEffect(() => {
    onExperienceStateChangeRef.current = onExperienceStateChange;
  }, [onExperienceStateChange]);
  const { settings } = useSettings();
  const settingsRef = useRef(settings);
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const pcName = pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`;
  const headerName = isAdmin
    ? `${pcInstance.player_index ?? '?'}: ${pcInstance.pc_name || 'Unknown PC'}`
    : (pcInstance.player_index?.toString() || '?');

  const run = state.run;
  const phase = state.phase;
  // Crash ladder, read exactly like PCTile reads the agent's crash_recovery
  // block: a relaunch in flight still counts as running (the visitor never
  // left), a gave-up crash frees the PC but keeps it out of dispatch while
  // its visitor is unresolved.
  const crashRecovery = fakeCrashRecovery(state);
  const crashView = crashRecoveryView(crashRecovery);
  const dispatchRunning = phase === 'loading' || phase === 'running' || phase === 'recovering';
  // No-VR drill: the fake agent's banner, shaped like the real one, and the
  // verify modal that opens from it. Verdicts on a fake incident id are not
  // written to the database (PCTile skips 'fake-' ids too).
  const [verifyOpen, setVerifyOpen] = useState(false);
  const noVr = state.noVr;
  const noVrNotification: Notification | null = noVr
    ? {
        id: noVr.category === 'stream_teardown' ? 'stream_teardown' : 'vr_no_stream',
        type: noVr.category === 'stream_teardown' ? 'error' : 'warning',
        text: noVr.notificationText,
        start_time: new Date().toISOString(),
        category: noVr.category,
        actions: ['repair', 'dismiss'],
        meta: { incident_id: noVr.incidentId, test: true },
      }
    : null;
  const handleVerifyAction = async (kind: NoVrVerdictKind, saw: NoVrSaw) => {
    if (kind === 'kick') {
      fakeAgentNoVrKick(pcInstance.id);
      onToast?.({ kind: 'success', message: `Streaming-herstart verstuurd naar headset van ${pcInstance.pc_name || 'PC'} (gezien: ${saw})` });
      return;
    }
    if (kind === 'kick_pc') {
      // The drill treats both-sides like the kick: same recovery in the fake agent.
      // Note this tile synthesises state client-side, so a green drill here proves
      // the UI wiring only — never the agent path (drills-that-synthesise-state-
      // cannot-prove-the-agent-side).
      fakeAgentNoVrKick(pcInstance.id);
      onToast?.({ kind: 'success', message: `Business Streaming herstart op ${pcInstance.pc_name || 'PC'} en headset (gezien: ${saw})` });
      return;
    }
    setVerifyOpen(false);
    if (kind === 'repair') {
      fakeAgentNoVrRepair(pcInstance.id);
      return;
    }
    fakeAgentNoVrDismiss(pcInstance.id);
    onToast?.({ kind: 'success', message: `Vals alarm vastgelegd voor ${pcInstance.pc_name || 'PC'} (gezien: ${saw})` });
  };

  // --- Launch (same contract as PCTile.handleLaunchExperience) --------------
  // Flip the fake agent first (the tile shows "Laden..." immediately), then
  // pop the queue: pinned entry from the dispatch pop-up, else the widget's
  // dequeue-on-launch selection. Resolves true: the fake launch cannot fail.
  const handleLaunchExperience = async (
    experienceId: string,
    version: string,
    language: string | null,
    opts?: { dequeueEntryId?: string; allowMove?: boolean },
  ): Promise<boolean> => {
    const release =
      experiences.find(
        (e) => e.experience_id === experienceId && (e.experience_release?.version || '1.0.0') === version,
      ) ?? experiences.find((e) => e.experience_id === experienceId);
    setError(null);
    setLanguagePick(null);
    fakeAgentLaunch(pcInstance.id, {
      experienceId,
      name: release?.experiences?.name || 'Onbekende ervaring',
      version,
      language,
      loadingMs: readFakeLoadingSeconds() * 1000,
      runMs: readFakeRunSeconds() * 1000,
    });

    const operatorId = currentOperatorId();
    const qc = queueCtxRef.current;
    // Same click-time resolve-and-claim the real tile does, so a staging drill
    // exercises the double-dequeue guard rather than only the happy path.
    const dequeueTargetId =
      opts?.dequeueEntryId ?? (qc?.dequeueOnLaunch ? qc.selectedEntry?.queue_entry_id : undefined);
    if (dequeueTargetId) qc?.claimEntry(dequeueTargetId);
    if (dequeueTargetId && operatorId) {
      try {
        await dequeueForLaunch(dequeueTargetId, operatorId, pcInstance.id, opts?.allowMove === true);
      } catch (err) {
        queueCtxRef.current?.releaseEntry(dequeueTargetId);
        if (isAlreadyStartedError(err)) {
          setError('Deze bezoeker speelt al op een andere PC — de wachtrij is niet bijgewerkt.');
        } else if (!isNotQueuedError(err)) {
          console.error('fake tile: queue dequeue after launch failed', err);
          setError('Launch gelukt, maar wachtrij niet bijgewerkt. Controleer handmatig.');
        }
      }
      qc?.setSelected(null);
    }
    return true;
  };
  const launchRef = useRef(handleLaunchExperience);
  launchRef.current = handleLaunchExperience;

  // Idle button click: 0/1 language launches directly, 2+ opens the picker.
  // Multiplayer releases launch like any other here (no MP session modal:
  // that flow needs real agents).
  const handleExperienceClick = (release: ConfigurationRelease) => {
    const langs = release.experiences?.supported_languages ?? [];
    const version = release.experience_release?.version || '1.0.0';
    if (langs.length === 0) {
      void handleLaunchExperience(release.experience_id, version, null);
    } else if (langs.length === 1) {
      void handleLaunchExperience(release.experience_id, version, langs[0].language?.short_name || null);
    } else {
      setLanguagePick(release);
    }
  };

  // --- Visitor from the queue (same guard as PCTile) --------------------------
  // A crashed run counts as "still playing here" until the operator resolves
  // it, exactly like on a real tile. Declared before the dispatch report,
  // which reads it.
  const queueVisitor: StartedEntry | null = useMemo(() => {
    if ((!dispatchRunning && phase !== 'gaveup') || !run) return null;
    const s = queueCtx?.startedByPc.get(pcInstance.id) ?? null;
    if (!s) return null;
    const eventAt = Date.parse(s.last_event_at || s.started_at);
    if (Number.isFinite(eventAt) && eventAt < run.launchedAt - 5 * 60_000) return null;
    return s;
  }, [dispatchRunning, phase, run, queueCtx, pcInstance.id]);

  // Same truth table as PCTile: relaunch in flight OR a gave-up crash whose
  // visitor is still on this PC.
  const dispatchRecovering = phase === 'recovering' || (phase === 'gaveup' && !!queueVisitor);

  // --- Dispatch wiring (mirrors PCTile) --------------------------------------
  useEffect(() => {
    dispatchCtxRef.current?.reportPC(pcInstance.id, {
      pcName: pcInstance.pc_name ?? pcInstance.id,
      pcLabel: headerName,
      online: true,
      running: dispatchRunning,
      canLaunch: true,
      recovering: dispatchRecovering,
    });
  }, [pcInstance.id, pcInstance.pc_name, dispatchRunning, dispatchRecovering]);
  useEffect(() => {
    onExperienceStateChangeRef.current?.(pcInstance.id, dispatchRunning);
  }, [pcInstance.id, dispatchRunning]);
  useEffect(() => {
    // Mount-scoped on purpose (see PCTile): the provider wraps the tiles.
    const pcId = pcInstance.id;
    dispatchCtxRef.current?.registerLaunch(pcId, (release, language, dequeueEntryId, allowMove) =>
      launchRef.current(
        release.experience_id,
        release.experience_release?.version || '1.0.0',
        language,
        { dequeueEntryId, allowMove },
      ),
    );
    return () => {
      dispatchCtxRef.current?.retractPC(pcId);
      dispatchCtxRef.current?.unregisterLaunch(pcId);
      onExperienceStateChangeRef.current?.(pcId, false);
    };
  }, [pcInstance.id]);

  // --- Nearly done: what PCTile does on the agent's last_level_warning ------
  useEffect(() => {
    if (phase !== 'running' || !run || run.endsAt === null || run.nearlyDoneFired) return;
    if (Date.now() < run.endsAt - FAKE_NEARLY_DONE_LEAD_MS) return;
    fakeAgentMarkNearlyDone(pcInstance.id);
    const head = queueCtxRef.current?.headEntry ?? null;
    if (head) {
      // Fans out to the dashboard banner, other tabs and the Electron display.
      broadcastNextUp({ displayName: queueDisplayName(head), pcName: headerName });
    }
    const s = settingsRef.current;
    if (s.nearlyDoneSoundEnabled) playDangerAlert(s.nearlyDoneVolume);
  }, [phase, run, state.tick, pcInstance.id, pcName]);

  const recoveryDeps = (): TileRecoveryDeps => ({
    pcId: pcInstance.id,
    pcName,
    pcLabel: headerName,
    experiences,
    dispatchCtx,
    queueCtx,
    stop: async () => {
      fakeAgentStop(pcInstance.id);
      return true;
    },
    // allowMove: "Opnieuw starten" re-points a visitor who is ALREADY started
    // (same PC, so started_pc_id does not even change). Without the flag the
    // RPC's new already_started guard would refuse the recovery relaunch, which
    // is the one case where re-pointing a live row is exactly the intent.
    relaunch: (experienceId, version, language, dequeueEntryId) =>
      handleLaunchExperience(experienceId, version, language, { dequeueEntryId, allowMove: true }),
    setBusy,
    setError,
    // Short pause so the idle -> loading flip is visible.
    relaunchDelayMs: 300,
  });
  const currentForRecovery = run ? { id: run.experienceId, version: run.version } : null;

  // STOP with the same confirm a real tile asks for.
  const handleStop = () => {
    if (!run) return;
    if (!window.confirm(`Are you sure you want to stop "${run.name}"?`)) return;
    setError(null);
    fakeAgentStop(pcInstance.id);
  };

  // Crash drill: first crash inside the window is auto-restarted with the same
  // visitor and the same remaining time, the next one trips the loop guard.
  const handleCrash = () => {
    const rung = fakeAgentCrash(pcInstance.id);
    if (!rung) return;
    onToast?.({
      kind: 'error',
      message:
        rung === 'gave_up'
          ? `${pcName}: auto-herstel gestopt (simulatie, 2e crash)`
          : `${pcName}: ervaring gecrasht (simulatie, exit 3), automatisch herstarten`,
    });
  };

  // --- Timers -------------------------------------------------------------------
  const now = Date.now();
  const elapsedS = run?.runStartedAt ? (now - run.runStartedAt) / 1000 : 0;
  const remainingS = run?.endsAt ? Math.max(0, (run.endsAt - now) / 1000) : 0;
  const runPct = run ? Math.min(100, (elapsedS / (run.runMs / 1000)) * 100) : 0;
  // Loading bar: climbs to 95% over the loading time and parks there (a stuck
  // start parks at 95% forever, like a real tile waiting for experience_loaded).
  const loadPct = run
    ? run.loadingMs > 0
      ? Math.min(95, ((now - run.launchedAt) / run.loadingMs) * 95)
      : 95
    : 0;
  const nearlyDone = phase === 'running' && !!run?.nearlyDoneFired;

  // `actions: false` keeps the visitor line but hides the buttons — the rung
  // where the agent is still relaunching and the operator decides nothing.
  const renderQueueVisitor = (opts?: { actions?: boolean }) => {
    if (!queueVisitor) return null;
    const marker = partyMarker(queueVisitor);
    return (
      <div className="tile-queue-visitor">
        <div className="tile-queue-visitor-line" title="Bezoeker uit de wachtrij die op deze PC speelt">
          <LanguageFlag code={queueVisitor.play_language} className="tile-queue-flag" />
          <span className="tile-queue-name">{fullName(queueVisitor)}</span>
          {marker ? <span className="tile-queue-party">{marker}</span> : null}
        </div>
        {opts?.actions !== false && (
        <div className="tile-queue-actions">
          <button
            className="tile-queue-btn"
            onClick={() => {
              if (currentForRecovery) void queueRestart(recoveryDeps(), queueVisitor, currentForRecovery);
            }}
            disabled={busy || !currentForRecovery}
            title="Stop en start dezelfde ervaring opnieuw voor deze bezoeker (zelfde taal, zelfde plek in de administratie)."
          >
            Opnieuw starten
          </button>
          <button
            className="tile-queue-btn"
            onClick={() => void queueMove(recoveryDeps(), queueVisitor, currentForRecovery)}
            disabled={busy || !dispatchCtx}
            title="Stop hier en kies een andere PC voor deze bezoeker."
          >
            Verplaats naar andere PC
          </button>
          <button
            className="tile-queue-btn"
            onClick={() => void queueRequeue(recoveryDeps(), queueVisitor)}
            disabled={busy}
            title="Stop en zet de bezoeker terug in de wachtrij op de oorspronkelijke plek."
          >
            Terug in wachtrij
          </button>
        </div>
        )}
      </div>
    );
  };

  // Same body as PCTile's crash ladder (styles in index.css).
  const renderCrashRecoveryBody = () => {
    if (!crashView) return null;
    const gaveUp = crashView.tone === 'gave_up';
    return (
      <div className={`crash-recovery-body${gaveUp ? ' crash-recovery-gaveup' : ''}`}>
        <div className="crash-recovery-headline">
          <span className="crash-recovery-icon" aria-hidden="true">{gaveUp ? '⛔' : '↻'}</span>
          <span>{crashView.text}</span>
        </div>
        <div className="crash-recovery-sub">
          {gaveUp
            ? 'De PC blijft bezet tot je dit voor deze bezoeker oplost.'
            : 'De bezoeker blijft op deze PC; de ervaring komt terug op dezelfde plek.'}
        </div>
        {run ? <div className="experience-name-v2">{run.name}</div> : null}
        {state.note ? <div className="crash-recovery-sub">{state.note}</div> : null}
        {renderQueueVisitor({ actions: gaveUp })}
      </div>
    );
  };

  // Constant per bundle, so hook order never changes across renders.
  if (!DEV_TOOLS_ENABLED) return null;

  // --- Queue-mode bar variant (same switch PCTile takes, after every hook) ----
  // The fake agent has no scenes, so the bar falls back to elapsed-over-run,
  // and it quotes the SIMULATED run's remaining time rather than the queue's
  // cycle estimate — a 60-second drill run must not claim "nog ~25 min".
  if (variant === 'bar') {
    const barVisitor: PCBarVisitor | null = queueVisitor
      ? {
          name: fullName(queueVisitor),
          language: queueVisitor.play_language,
          party: partyMarker(queueVisitor),
          experienceName: run?.name ?? queueVisitor.experience_names ?? null,
        }
      : null;
    // Only the PC the queue column pairs the head with names that visitor
    // (free now, first by name) — see components/PCTile for why.
    const nextEntry = queueCtx?.selectedEntry ?? null;
    // Same single source the real tile reads (services/dispatchPick).
    const firstFreePcId = dispatchCtx?.nextPcId ?? null;
    const barNext: PCBarVisitor | null =
      nextEntry && firstFreePcId === pcInstance.id
        ? {
            name: fullName(nextEntry),
            language: nextEntry.play_language,
            party: partyMarker(nextEntry),
            experienceName: nextEntry.experience_names,
          }
        : null;

    let barState: PCBarState;
    let barProblemText: string | null = null;
    let barStatusText: string | null = null;
    let barIdleText: string | null = null;
    let barFill: number | null = null;
    // Same split status the real bar uses: elapsed leads, estimate trails.
    let barShowRunStatus = false;
    // Same ladder order as PCTile: an agent error notification is the bar's red
    // state (the no-VR drill banner here), then the running checks, then the
    // crash record, so a stale crash can never paint a busy PC red.
    if (noVr) {
      barState = 'problem';
      barProblemText = noVr.repair?.running
        ? `Auto-herstel bezig · ${noVr.repair.phase}`
        : noVr.repair?.needsReboot
          ? `${noVr.notificationText} · herstel mislukt, herstart PC`
          : noVr.notificationText;
    } else if (phase === 'running' && run) {
      barState = nearlyDone ? 'nearly' : 'running';
      barFill = runPct / 100;
      // Same as the real bar: the end-of-run message takes the scene slot, the
      // clock keeps running next to it.
      barShowRunStatus = true;
      if (nearlyDone) barStatusText = 'Bijna klaar · bril klaarmaken';
    } else if (phase === 'loading' && run) {
      barState = 'loading';
      barFill = loadPct / 100;
      barStatusText = `Laden…${run.stuck ? ' (vastloper)' : ''}`;
    } else if (crashView) {
      barState = 'problem';
      barProblemText =
        crashView.tone === 'gave_up'
          ? `${crashView.text} · de PC blijft bezet tot je dit oplost`
          : crashView.text;
    } else if (phase === 'done') {
      barState = 'nearly';
      barFill = 1;
      barStatusText = 'Bezoeker is klaar · bril ophalen';
    } else {
      barState = 'free';
      barIdleText = 'Vrij';
    }

    const barRecoveryActions: PCBarAction[] =
      queueVisitor && phase !== 'recovering'
        ? [
            {
              label: 'Opnieuw starten',
              onClick: () => {
                if (currentForRecovery) void queueRestart(recoveryDeps(), queueVisitor, currentForRecovery);
              },
              disabled: busy || !currentForRecovery,
              tone: 'blue',
            },
            {
              label: 'Verplaats naar andere PC',
              onClick: () => void queueMove(recoveryDeps(), queueVisitor, currentForRecovery),
              disabled: busy || !dispatchCtx,
              tone: 'blue',
            },
            {
              label: 'Terug in wachtrij',
              onClick: () => void queueRequeue(recoveryDeps(), queueVisitor),
              disabled: busy,
              tone: 'neutral',
            },
          ]
        : [];

    if (noVr) {
      barRecoveryActions.unshift(
        {
          label: '🔍 Controleer beeld',
          onClick: () => setVerifyOpen(true),
          disabled: !!noVr.repair?.running,
          tone: 'blue',
          title: 'Kijk mee in de headset en kies de stap',
        },
        {
          label: noVr.repair?.needsReboot ? '⟳ Herstart PC (simulatie)' : '🔧 Auto-herstel',
          onClick: () => (noVr.repair?.needsReboot ? fakeAgentNoVrDismiss(pcInstance.id) : fakeAgentNoVrRepair(pcInstance.id)),
          disabled: !!noVr.repair?.running,
          tone: 'blue',
        },
        { label: '✕ Sluiten', onClick: () => fakeAgentNoVrDismiss(pcInstance.id), disabled: !!noVr.repair?.running, tone: 'neutral' },
      );
    }

    const barPrimary: PCBarAction | null =
      barState === 'free'
        ? {
            label: 'Start',
            onClick: () => dispatchCtx?.openDispatch(pcInstance.id),
            disabled: !dispatchCtx || !nextEntry || busy,
            title: nextEntry ? `Start ${fullName(nextEntry)} op deze PC` : 'Niemand in de wachtrij',
            tone: barNext ? 'go' : 'quiet',
          }
        : null;

    const SIM_ONLY = 'Niet beschikbaar op een gesimuleerde PC';
    const gateOf = (onClick: () => void, checks: [boolean, string][]): InterveneAction => {
      const failed = checks.find(([ok]) => !ok);
      return { onClick, disabled: !!failed, reason: failed ? failed[1] : null };
    };
    const noVisitor = 'Geen bezoeker uit de wachtrij op deze PC';

    return (
      <>
        <PCBarView
          pcName={headerName}
          compactPc={!isAdmin}
          /* The simulator has no real devices; report healthy ones so the
             drill exercises the same layout as a real bar. */
          devices={[
            { key: 'hmd', pct: 92, tone: 'ok', title: 'Bril 92% (simulatie)' },
            { key: 'audio', pct: 78, tone: 'ok', title: 'Koptelefoon 78% (simulatie)' },
          ]}
          state={barState}
          visitor={barVisitor}
          nextVisitor={barState === 'free' ? barNext : null}
          experienceName={run?.name ?? null}
          progress={barFill}
          statusText={barStatusText}
          statusScene={barShowRunStatus && nearlyDone ? barStatusText : null}
          statusElapsed={barShowRunStatus ? formatClock(elapsedS) : null}
          statusRemaining={
            barShowRunStatus && remainingS >= 60
              ? `nog ~${Math.round(remainingS / 60)} min`
              : null
          }
          problemText={barProblemText}
          recoveryActions={barRecoveryActions}
          primaryAction={barPrimary}
          idleText={barIdleText}
          badges={[{ key: 'sim', label: 'SIMULATIE', tone: 'loading', title: 'Gesimuleerde PC (staging): geen agent, geen headset.' }]}
          onIntervene={() => setShowIntervene(true)}
          interveneTitle={`Ingrijpen op ${headerName} (simulatie)`}
          errorText={error}
        />
        {/* Drill strip: the square tile's footer controls, under the bar */}
        <div className="pc-tile-footer fake-tile-drill fake-tile-drill-bar">
          <label className="fake-tile-drill-toggle" title="De volgende start blijft in Laden... hangen tot je stopt (geen injectie). Eenmalig.">
            <input type="checkbox" checked={state.stuckArmed} onChange={(e) => fakeAgentArmStuck(pcInstance.id, e.target.checked)} />
            Vastloper
          </label>
          <button className="fake-tile-drill-btn" onClick={handleCrash} disabled={phase !== 'loading' && phase !== 'running'} title="Laat de lopende ervaring crashen (exit 3).">
            Crash
          </button>
          <select
            className="fake-tile-drill-select"
            value={state.noVrOutcome}
            onChange={(e) => fakeAgentArmNoVrOutcome(pcInstance.id, e.target.value as FakeNoVrOutcome)}
            title="Welke stap 'werkt' bij de volgende Geen VR-drill."
          >
            <option value="kick">kick helpt</option>
            <option value="repair">herstel helpt</option>
            <option value="reboot">herstart nodig</option>
          </select>
          <button
            className="fake-tile-drill-btn"
            onClick={() => {
              if (!fakeAgentNoVr(pcInstance.id)) onToast?.({ kind: 'error', message: 'Geen VR-drill: start eerst een ervaring op deze tegel' });
            }}
            disabled={phase !== 'running' || !!noVr}
            title="Laat de tegel 'geen VR-stream' melden zoals de agent dat doet."
          >
            Geen VR
          </button>
        </div>
        {verifyOpen && noVrNotification && (
          <NoVrVerifyModal
            pcName={pcInstance.pc_name || 'Simulatie'}
            notification={noVrNotification}
            liveView={null}
            castable={false}
            castHint="Simulatie: geen live beeld. Kies hieronder wat je 'zag'; de gekozen stap loopt volgens de ingestelde uitkomst."
            busy={false}
            canRestartStreaming
            repairStatus={noVr?.repair ?? null}
            onAction={handleVerifyAction}
            onClose={() => setVerifyOpen(false)}
          />
        )}
        {showIntervene && (
          <InterveneSheet
            pcName={`${headerName} (simulatie)`}
            state={barState}
            visitor={barVisitor}
            experienceName={run?.name ?? null}
            /* Without a queue visitor the sheet collapses "Deze bezoeker" to one
               quiet line; runActive keeps Stop reachable for a walk-in run. */
            runActive={!!run}
            metaLine={state.note ?? null}
            errorText={error}
            onClose={() => setShowIntervene(false)}
            onRestart={gateOf(
              () => {
                if (queueVisitor && currentForRecovery) {
                  void queueRestart(recoveryDeps(), queueVisitor, currentForRecovery);
                }
              },
              [[!!queueVisitor, noVisitor], [!!currentForRecovery, 'Geen ervaring om opnieuw te starten'], [!busy, 'Bezig…']],
            )}
            onMove={gateOf(
              () => { if (queueVisitor) void queueMove(recoveryDeps(), queueVisitor, currentForRecovery); },
              [[!!queueVisitor, noVisitor], [!!dispatchCtx, 'Wachtrij staat uit'], [!busy, 'Bezig…']],
            )}
            onRequeue={gateOf(
              () => { if (queueVisitor) void queueRequeue(recoveryDeps(), queueVisitor); },
              [[!!queueVisitor, noVisitor], [!busy, 'Bezig…']],
            )}
            onStop={gateOf(handleStop, [[!!run, 'Er draait niets op deze PC'], [!busy, 'Bezig…']])}
            onRestartPC={gateOf(() => {}, [[false, SIM_ONLY]])}
            onRebootHmd={gateOf(() => {}, [[false, SIM_ONLY]])}
            onAutoRepair={gateOf(() => {}, [[false, SIM_ONLY]])}
            onInvalidateRun={gateOf(() => {}, [[false, SIM_ONLY]])}
            onReportIssue={gateOf(() => {}, [[false, SIM_ONLY]])}
          />
        )}
      </>
    );
  }

  const tileClass =
    `pc-tile online fake-tile` +
    (phase === 'gaveup' ? ' fake-tile-crashed' : '') +
    (phase === 'done' ? ' fake-tile-done' : '');

  return (
    <div className={tileClass}>
      {/* Header: same frame as PCTile, SIMULATIE instead of the BRIL/HP badges */}
      <div className="pc-tile-header-v2">
        <span className="player-index">{headerName}</span>
        <div className="device-batteries">
          <span
            className="battery-indicator fake-tile-badge"
            title="Gesimuleerde PC (staging): geen agent, geen headset. Alle releases van de ruimte gelden als geinstalleerd."
          >
            SIMULATIE
          </span>
        </div>
      </div>

      {nearlyDone && (
        <div className="notifications-container-v2">
          <div className="notification-v2 notification-warning">
            <span className="notification-icon-v2">⚠</span>
            <span className="notification-text-v2">Bezoeker is bijna klaar</span>
          </div>
        </div>
      )}

      {/* Gave up with no queue visitor (queue off / walk-in): the tile stays a
          normal free PC and a fresh launch clears the crash, like a real one. */}
      {crashView && !dispatchRecovering && (
        <div className="notifications-container-v2">
          <div className="notification-v2 notification-error">
            <span className="notification-icon-v2">⛔</span>
            <span className="notification-text-v2">{crashView.text}</span>
          </div>
        </div>
      )}

      <div className="pc-tile-body">
        {phase === 'running' && run ? (
          <div className="experience-running-v2">
            <div className="experience-running-info">
              <div className="experience-running-left">
                <div className="experience-name-v2">{run.name}</div>
                {run.language && (
                  <div className="experience-scene-v2">Taal: {run.language.toUpperCase()}</div>
                )}
              </div>
              <div className="experience-running-right">
                <div className="time-display">
                  <span className="time-elapsed">{formatClock(elapsedS)}</span>
                  <span className="time-separator"> / </span>
                  <span className="time-remaining">{formatClock(remainingS)}</span>
                </div>
              </div>
            </div>
            <div className="mini-progress-bar">
              <div className="mini-progress-fill" style={{ width: `${runPct}%` }} />
            </div>
            <div className="experience-running-actions">
              <button className="stop-btn-v2" onClick={handleStop} disabled={busy}>
                STOP
              </button>
            </div>
            {renderQueueVisitor()}
          </div>
        ) : phase === 'loading' && run ? (
          <div className="experience-loading-v2">
            <div className="experience-name-v2">{run.name}</div>
            <div className="fake-tile-loading">
              <span className="fake-tile-spinner" aria-hidden="true" />
              <span>Laden...{run.stuck ? ' (vastloper)' : ''}</span>
            </div>
            <div className="launch-progress-container-v2">
              <div className="launch-progress-bar-v2">
                <div className="launch-progress-fill-v2" style={{ width: `${loadPct}%` }} />
              </div>
              <span className="launch-progress-percent-v2">{Math.round(loadPct)}%</span>
            </div>
            <button className="stop-btn-v2" onClick={handleStop} disabled={busy}>
              STOP
            </button>
            {renderQueueVisitor()}
          </div>
        ) : phase === 'done' ? (
          <div className="fake-tile-flash fake-tile-flash-done">
            {run && <div className="experience-name-v2">{run.name}</div>}
            <div className="fake-tile-flash-text">Klaar</div>
          </div>
        ) : dispatchRecovering ? (
          /* Crashed: relaunch in flight, or the ladder gave up on this visitor */
          renderCrashRecoveryBody()
        ) : (
          <div className="experience-selection-v2">
            {experiences.length > 0 ? (
              <div className={`experience-launch-grid-v2${experiences.length > 2 ? ' compact' : ''}`}>
                {experiences.map((release) => {
                  const langs = release.experiences?.supported_languages ?? [];
                  const languageFlag = langs.length === 1 ? langs[0].language?.short_name : null;
                  const expColor = nameToColor(release.experiences?.name || 'Unknown');
                  const colorStyle = {
                    '--btn-color': expColor.border,
                    '--btn-bg': expColor.bg,
                  } as React.CSSProperties;
                  return (
                    <button
                      key={release.id}
                      className="launch-btn-v2"
                      style={colorStyle}
                      onClick={() => handleExperienceClick(release)}
                      disabled={busy}
                    >
                      <span className="launch-btn-name-v2">
                        {release.experiences?.name || 'Unknown'}
                        {isAdmin && release.experience_release?.version && (
                          <span className="version-tag">v{release.experience_release.version}</span>
                        )}
                        {languageFlag && (
                          <span className="language-flag-v2">{languageFlag.toUpperCase()}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="no-experiences-v2">No experiences available</p>
            )}
          </div>
        )}
      </div>

      {/* Language picker (2+ languages), same markup as PCTile */}
      {languagePick && (
        <div className="language-selection-modal">
          <div className="language-selection-content">
            <h3>Select Language</h3>
            <p className="experience-name">{languagePick.experiences?.name}</p>
            <div className="language-buttons">
              {(languagePick.experiences?.supported_languages ?? []).map((ls) => (
                <button
                  key={ls.id}
                  className="language-btn"
                  onClick={() =>
                    void handleLaunchExperience(
                      languagePick.experience_id,
                      languagePick.experience_release?.version || '1.0.0',
                      ls.language?.short_name || null,
                    )
                  }
                  disabled={busy}
                >
                  {hasLanguageFlag(ls.language?.short_name) ? (
                    <LanguageFlag code={ls.language?.short_name} className="language-flag-img-large" />
                  ) : (
                    <span className="language-flag-large">{(ls.language?.short_name || '').toUpperCase()}</span>
                  )}
                  <span className="language-name">{ls.language?.name}</span>
                </button>
              ))}
            </div>
            <button className="cancel-lang-btn" onClick={() => setLanguagePick(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* No-VR drill banner: same row the real tile renders, same verify modal */}
      {noVrNotification && (
        <div className="notifications-container-v2">
          <div className={`notification-v2 notification-${noVrNotification.type}`}>
            <span className="notification-icon-v2">{noVrNotification.type === 'error' ? '⚠️' : '⚠'}</span>
            <span className="notification-text-v2">{noVrNotification.text}</span>
            <span className="notification-action-v2">
              {!noVr?.repair?.running && (
                <button className="notification-repair-btn" onClick={() => setVerifyOpen(true)} title="Kijk mee in de headset en kies de stap">
                  🔍 Controleer
                </button>
              )}
              {!noVr?.repair && (
                <button className="notification-repair-btn" onClick={() => fakeAgentNoVrRepair(pcInstance.id)}>
                  🔧 Auto-herstel
                </button>
              )}
              {noVr?.repair?.running && <span className="notification-repair-progress">⏳ Herstellen… ({noVr.repair.phase})</span>}
              {noVr?.repair && !noVr.repair.running && noVr.repair.recovered && <span className="notification-repair-ok">✓ Hersteld</span>}
              {noVr?.repair && !noVr.repair.running && noVr.repair.needsReboot && (
                <button className="notification-repair-btn notification-repair-reboot" onClick={() => fakeAgentNoVrDismiss(pcInstance.id)}>
                  ⟳ Herstel mislukt — herstart PC
                </button>
              )}
              {!noVr?.repair?.running && (
                <button className="notification-repair-btn notification-dismiss-btn" onClick={() => fakeAgentNoVrDismiss(pcInstance.id)} title="Sluiten">
                  ✕
                </button>
              )}
            </span>
          </div>
        </div>
      )}
      {verifyOpen && noVrNotification && (
        <NoVrVerifyModal
          pcName={pcInstance.pc_name || 'Simulatie'}
          notification={noVrNotification}
          liveView={null}
          castable={false}
          castHint="Simulatie: geen live beeld. Kies hieronder wat je 'zag'; de gekozen stap loopt volgens de ingestelde uitkomst."
          busy={false}
          canRestartStreaming
          repairStatus={noVr?.repair ?? null}
          onAction={handleVerifyAction}
          onClose={() => setVerifyOpen(false)}
        />
      )}

      {/* Drill controls instead of the volume footer */}
      <div className="pc-tile-footer fake-tile-drill">
        <label
          className="fake-tile-drill-toggle"
          title="De volgende start blijft in Laden... hangen tot je stopt (geen injectie). Eenmalig."
        >
          <input
            type="checkbox"
            checked={state.stuckArmed}
            onChange={(e) => fakeAgentArmStuck(pcInstance.id, e.target.checked)}
          />
          Vastloper
        </label>
        <button
          className="fake-tile-drill-btn"
          onClick={handleCrash}
          disabled={phase !== 'loading' && phase !== 'running'}
          title="Laat de lopende ervaring crashen (exit 3). Eerste keer: automatisch herstarten met dezelfde bezoeker en dezelfde resterende tijd. Nog een crash binnen 2 minuten: auto-herstel stopt en je lost het met de herstelknoppen op."
        >
          Crash
        </button>
        <select
          className="fake-tile-drill-select"
          value={state.noVrOutcome}
          onChange={(e) => fakeAgentArmNoVrOutcome(pcInstance.id, e.target.value as FakeNoVrOutcome)}
          title="Welke stap 'werkt' bij de volgende Geen VR-drill: de headset-kick, het auto-herstel, of niets (herstart PC nodig)."
        >
          <option value="kick">kick helpt</option>
          <option value="repair">herstel helpt</option>
          <option value="reboot">herstart nodig</option>
        </select>
        <button
          className="fake-tile-drill-btn"
          onClick={() => {
            if (!fakeAgentNoVr(pcInstance.id)) onToast?.({ kind: 'error', message: 'Geen VR-drill: start eerst een ervaring op deze tegel' });
          }}
          disabled={phase !== 'running' || !!noVr}
          title="Laat de tegel 'geen VR-stream' melden zoals de agent dat doet, met de controle-modal en het herstelverloop."
        >
          Geen VR
        </button>
      </div>

      {error && <div className="error-message-v2">{error}</div>}
    </div>
  );
}

// Memoised for the same reason as PCTile: staging rooms render a full grid of
// these, and none of them should re-render because a sibling polled.
export default React.memo(FakePCTile);
